import React, { createContext, useContext, useState, useCallback } from "react";

/**
 * ChatPopupContext
 * 
 * Manages multiple floating chat popups that can be opened from anywhere in the app.
 * Supports:
 * - Opening multiple chats simultaneously
 * - Each popup tied to a specific entity (place/event/user)
 * - Horizontal stacking of popups
 * - Independent state for each popup
 */

const ChatPopupContext = createContext(null);

// Maximum number of popups that can be open at once
const MAX_POPUPS = 4;

// Popup width for positioning calculations
const POPUP_WIDTH = 340;
const POPUP_GAP = 12;

export const ChatPopupProvider = ({ children }) => {
  // Array of open chat popups
  // Each popup: { id, chatId, entityType, entityData, minimized, maximized, position }
  const [openPopups, setOpenPopups] = useState([]);

  // Cache conversation IDs to avoid re-fetching when reopening
  // Map: chatId -> conversationId
  const conversationIdCache = React.useRef(new Map());

  // Cache messages to preserve them when popup is closed and reopened
  // Map: chatId -> { messages: [], lastUpdated: timestamp }
  const messagesCache = React.useRef(new Map());

  /**
   * Cache a conversation ID for a chat
   * @param {string} chatId - Chat/entity ID
   * @param {string} conversationId - Conversation ID from API
   */
  const cacheConversationId = useCallback((chatId, conversationId) => {
    if (chatId && conversationId) {
      conversationIdCache.current.set(chatId, conversationId);
    }
  }, []);

  /**
   * Get cached conversation ID
   * @param {string} chatId - Chat/entity ID
   * @returns {string|null} - Cached conversation ID or null
   */
  const getCachedConversationId = useCallback((chatId) => {
    return conversationIdCache.current.get(chatId) || null;
  }, []);

  /**
   * Cache messages for a chat
   * @param {string} chatId - Chat/entity ID
   * @param {Array} messages - Messages array
   */
  const cacheMessages = useCallback((chatId, messages) => {
    if (chatId && Array.isArray(messages)) {
      messagesCache.current.set(chatId, {
        messages,
        lastUpdated: Date.now(),
      });
    }
  }, []);

  /**
   * Get cached messages
   * @param {string} chatId - Chat/entity ID
   * @returns {Array|null} - Cached messages or null
   */
  const getCachedMessages = useCallback((chatId) => {
    const cached = messagesCache.current.get(chatId);
    if (cached) {
      // Return cached messages if they're less than 5 minutes old
      const maxAge = 5 * 60 * 1000; // 5 minutes
      if (Date.now() - cached.lastUpdated < maxAge) {
        return cached.messages;
      }
      // Clear stale cache
      messagesCache.current.delete(chatId);
    }
    return null;
  }, []);

  /**
   * Clear cached messages for a chat
   * @param {string} chatId - Chat/entity ID
   */
  const clearMessagesCache = useCallback((chatId) => {
    if (chatId) {
      messagesCache.current.delete(chatId);
    }
  }, []);

  /**
   * Open a new chat popup or focus existing one
   * @param {Object} params - Chat parameters
   * @param {string} params.chatId - Unique chat/conversation ID
   * @param {string} params.entityType - Type: 'place', 'event', 'user', 'business'
   * @param {Object} params.entityData - Entity data (name, image, etc.)
   */
  const openChatPopup = useCallback(({ chatId, entityType, entityData }) => {
    setOpenPopups((prev) => {
      // Check if popup already exists (including closed ones)
      const existingIndex = prev.findIndex((p) => p.chatId === chatId);
      
      if (existingIndex !== -1) {
        // Popup exists - reopen, bring to front and unminimize
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          minimized: false,
          maximized: false, // Reset to normal size when reopening
          closed: false, // Reopen if it was closed
        };
        // Move to end of array (front)
        const [popup] = updated.splice(existingIndex, 1);
        updated.push(popup);
        return updated;
      }

      // Check if we've reached max popups (count only non-closed popups)
      const activePopups = prev.filter((p) => !p.closed);
      if (activePopups.length >= MAX_POPUPS) {
        // Remove the oldest non-closed popup completely
        const oldestActiveIndex = prev.findIndex((p) => !p.closed);
        if (oldestActiveIndex !== -1) {
          const updated = [...prev];
          updated.splice(oldestActiveIndex, 1); // Actually remove oldest
          updated.push({
            id: `popup-${Date.now()}`,
            chatId,
            entityType,
            entityData,
            minimized: false,
            maximized: false,
            closed: false,
          });
          return updated;
        }
      }

      // Add new popup
      return [
        ...prev,
        {
          id: `popup-${Date.now()}`,
          chatId,
          entityType,
          entityData,
          minimized: false,
          maximized: false,
          closed: false,
        },
      ];
    });
  }, []);

  /**
   * Close a specific chat popup
   * @param {string} popupId - Popup ID to close
   * Instead of removing it, mark it as closed to preserve component state
   */
  const closeChatPopup = useCallback((popupId) => {
    setOpenPopups((prev) => 
      prev.map((p) => 
        p.id === popupId ? { ...p, closed: true } : p
      )
    );
  }, []);

  /**
   * Close popup by chat ID (mark as closed to preserve state)
   * @param {string} chatId - Chat ID to close
   */
  const closeChatPopupByChatId = useCallback((chatId) => {
    setOpenPopups((prev) => 
      prev.map((p) => 
        p.chatId === chatId ? { ...p, closed: true } : p
      )
    );
  }, []);

  /**
   * Permanently remove a closed popup (for cleanup)
   * @param {string} chatId - Chat ID to remove
   */
  const removeClosedPopup = useCallback((chatId) => {
    setOpenPopups((prev) => prev.filter((p) => p.chatId !== chatId));
  }, []);

  /**
   * Toggle minimize state of a popup
   * @param {string} popupId - Popup ID to toggle
   */
  const toggleMinimize = useCallback((popupId) => {
    setOpenPopups((prev) =>
      prev.map((p) =>
        p.id === popupId ? { ...p, minimized: !p.minimized } : p
      )
    );
  }, []);

  /**
   * Toggle maximize state of a popup
   * @param {string} popupId - Popup ID to toggle
   */
  const toggleMaximize = useCallback((popupId) => {
    setOpenPopups((prev) =>
      prev.map((p) =>
        p.id === popupId ? { ...p, maximized: !p.maximized, minimized: false } : p
      )
    );
  }, []);

  /**
   * Minimize all popups
   */
  const minimizeAll = useCallback(() => {
    setOpenPopups((prev) =>
      prev.map((p) => ({ ...p, minimized: true, maximized: false }))
    );
  }, []);

  /**
   * Close all popups (mark as closed to preserve state)
   */
  const closeAll = useCallback(() => {
    setOpenPopups((prev) => prev.map((p) => ({ ...p, closed: true })));
  }, []);

  /**
   * Check if a chat is already open
   * @param {string} chatId - Chat ID to check
   * @returns {boolean}
   */
  const isChatOpen = useCallback(
    (chatId) => openPopups.some((p) => p.chatId === chatId),
    [openPopups]
  );

  /**
   * Get popup position based on index
   * @param {number} index - Popup index
   * @returns {Object} - { right, bottom }
   */
  const getPopupPosition = useCallback((index) => {
    const right = 20 + index * (POPUP_WIDTH + POPUP_GAP);
    return { right, bottom: 0 };
  }, []);

  // Cleanup closed popups after 30 minutes to prevent memory leaks
  React.useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;
      setOpenPopups((prev) => 
        prev.filter((p) => {
          // Remove popups that have been closed for more than 30 minutes
          if (p.closed && p.closedAt && p.closedAt < thirtyMinutesAgo) {
            return false;
          }
          return true;
        })
      );
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(cleanupInterval);
  }, []);

  // Update close functions to add closedAt timestamp
  const closeChatPopupWithTimestamp = useCallback((popupId) => {
    setOpenPopups((prev) => 
      prev.map((p) => 
        p.id === popupId ? { ...p, closed: true, closedAt: Date.now() } : p
      )
    );
  }, []);

  const value = {
    openPopups,
    openChatPopup,
    closeChatPopup: closeChatPopupWithTimestamp,
    closeChatPopupByChatId,
    toggleMinimize,
    toggleMaximize,
    minimizeAll,
    closeAll,
    isChatOpen,
    getPopupPosition,
    cacheConversationId,
    getCachedConversationId,
    cacheMessages,
    getCachedMessages,
    clearMessagesCache,
    removeClosedPopup,
    POPUP_WIDTH,
  };

  return (
    <ChatPopupContext.Provider value={value}>
      {children}
    </ChatPopupContext.Provider>
  );
};

export const useChatPopup = () => {
  const context = useContext(ChatPopupContext);
  if (!context) {
    throw new Error("useChatPopup must be used within a ChatPopupProvider");
  }
  return context;
};

export default ChatPopupContext;
